import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Box,
  Chip,
  Autocomplete,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  CircularProgress,
  Slider
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { Project } from "../types/projects";
import { useAddProjectMutation, useUpdateProjectMutation } from "../api/projectsApi";
import { useGetUnitTypesQuery } from "../../UnitTypes/api/unitTypesApi";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  project?: Project | null;
}

type FormUnitType = {
  id: number;
  name: string;
};

type LegalDocument = {
  title: string;
  description: string;
  filePath: File | null;
};

const AddEditProjects: React.FC<Props> = ({ open, setModalOpen, onSuccess, project }) => {
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    latitude: number;
    longitude: number;
    construction_status: string;
    amenities: string[];
    unit_types: FormUnitType[];
    images: File[];
    existingImages: Project["images"];
    projectImage: File | null;
    existingProjectImageUrl: string | null;
    number_of_units: number;
    completion_year: number;
    featured: boolean;
    location: string;
    legalDocuments: LegalDocument[];
    visit_price: number;
    progress: number;
  }>({
    title: "",
    description: "",
    latitude: 0,
    longitude: 0,
    construction_status: "",
    amenities: [],
    unit_types: [],
    images: [],
    existingImages: [],
    projectImage: null,
    existingProjectImageUrl: null,
    number_of_units: 0,
    completion_year: new Date().getFullYear(),
    featured: false,
    location: "",
    legalDocuments: [{ title: "", description: "", filePath: null }],
    visit_price: 0,
    progress: 0,
  });

  const { data: unitTypes } = useGetUnitTypesQuery();
  const [createProject] = useAddProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title,
        description: project.description,
        latitude: project.latitude,
        longitude: project.longitude,
        construction_status: project.construction_status,
        amenities: project.amenities,
        unit_types: project.unit_types.map((ut) => ({ id: ut.id, name: ut.name })),
        images: [],
        existingImages: project.images || [],
        projectImage: null,
        existingProjectImageUrl: project.project_image || null,
        number_of_units: project.number_of_units || 0,
        completion_year: project.completion_year || new Date().getFullYear(),
        featured: project.featured || false,
        location: project.location || "",
        legalDocuments: [], // Empty for edit mode
        visit_price: project.visit_price || 0,
        progress: project.progress || 0,
      });
    } else {
      setFormData({
        title: "",
        description: "",
        latitude: 0,
        longitude: 0,
        construction_status: "",
        amenities: [],
        unit_types: [],
        images: [],
        existingImages: [],
        projectImage: null,
        existingProjectImageUrl: null,
        number_of_units: 0,
        completion_year: new Date().getFullYear(),
        featured: false,
        location: "",
        legalDocuments: [{ title: "", description: "", filePath: null }],
        visit_price: 0,
        progress: 0,
      });
    }
  }, [project, open]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
  };

  const handleProjectImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        projectImage: file,
        existingProjectImageUrl: null, // reset existing when new selected
      }));
    }
  };

  const removeNewImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((img) => img.id !== id),
    }));
  };

  const removeProjectImage = () => {
    setFormData((prev) => ({
      ...prev,
      projectImage: null,
      existingProjectImageUrl: null,
    }));
  };

  const handleLegalDocumentChange = (index: number, field: keyof LegalDocument, value: string | File) => {
    setFormData(prev => {
      const updatedDocuments = [...prev.legalDocuments];
      updatedDocuments[index] = {
        ...updatedDocuments[index],
        [field]: value
      };
      
      // Add a new empty document if the current one is being filled (except for the last one)
      if (field === 'title' && value && index === updatedDocuments.length - 1) {
        updatedDocuments.push({ title: "", description: "", filePath: null });
      }
      
      return {
        ...prev,
        legalDocuments: updatedDocuments
      };
    });
  };

  const handleLegalDocumentFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLegalDocumentChange(index, 'filePath', file);
    }
  };

  const removeLegalDocument = (index: number) => {
    setFormData(prev => ({
      ...prev,
      legalDocuments: prev.legalDocuments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const fd = new FormData();
    fd.append("title", formData.title);
    fd.append("description", formData.description);
    fd.append("latitude", formData.latitude.toString());
    fd.append("longitude", formData.longitude.toString());
    fd.append("construction_status", formData.construction_status);
    fd.append("number_of_units", formData.number_of_units.toString());
    fd.append("completion_year", formData.completion_year.toString());
    fd.append("featured", formData.featured ? "1" : "0");
    fd.append("location", formData.location);
    fd.append("visit_price", formData.visit_price.toString());
    fd.append("progress", formData.progress.toString());
    
    formData.amenities.forEach((a, i) => fd.append(`amenities[${i}]`, a));
    formData.unit_types.forEach((ut) => fd.append('unit_types[]', ut.id.toString()));
    formData.images.forEach((file, i) => fd.append(`images[${i}]`, file));
    formData.existingImages.forEach((img, i) => fd.append(`existing_image_ids[${i}]`, img.id.toString()));
    
    if (formData.projectImage) {
      fd.append("project_image", formData.projectImage);
    }

    // Append legal documents
    formData.legalDocuments
      .filter(doc => doc.title && doc.filePath) // Only include documents with title and file
      .forEach((doc, i) => {
        fd.append(`legalDocuments[${i}][title]`, doc.title);
        fd.append(`legalDocuments[${i}][short_description]`, doc.description);
        fd.append(`legalDocuments[${i}][file_path]`, doc.filePath as File);
      });

    try {
      if (project?.id) {
        await updateProject({ id: project.id, body: fd }).unwrap();
      } else {
        await createProject({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      console.error("Error saving project:", err);
      setSubmissionError("Failed to create projects. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseError = () => {
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={setModalOpen} maxWidth="sm" fullWidth>
      <DialogTitle>{project ? "Edit Project" : "Add Project"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          multiline
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            margin="dense"
            label="Latitude"
            type="number"
            value={formData.latitude}
            onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Longitude"
            type="number"
            value={formData.longitude}
            onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
          />
        </Box>
        <TextField
          fullWidth
          margin="dense"
          label="Location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
        <TextField
          select
          fullWidth
          margin="dense"
          label="Construction Status"
          value={formData.construction_status}
          onChange={(e) => setFormData({ ...formData, construction_status: e.target.value })}
        >
          {['Planned', 'In Progress', 'Completed'].map(status => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </TextField>

        {formData.construction_status === 'In Progress' && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography gutterBottom>Progress: {formData.progress}%</Typography>
            <Slider
              value={formData.progress}
              onChange={(_, value) => setFormData({ ...formData, progress: value as number })}
              aria-label="Progress"
              valueLabelDisplay="auto"
              step={1}
              marks
              min={0}
              max={100}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            margin="dense"
            label="Number of Units"
            type="number"
            value={formData.number_of_units}
            onChange={(e) => setFormData({ ...formData, number_of_units: parseInt(e.target.value) || 0 })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Completion Year"
            type="number"
            value={formData.completion_year}
            onChange={(e) => setFormData({ ...formData, completion_year: parseInt(e.target.value) || new Date().getFullYear() })}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
            />
          }
          label="Featured Project"
        />

        <Autocomplete
          multiple
          options={unitTypes?.data ?? []}
          getOptionLabel={(option: any) => option.name}
          value={formData.unit_types || []}
          onChange={(e: any, value: any) => setFormData((prev) => ({ ...prev, unit_types: value }))}
          renderTags={(value: any, getTagProps: any) =>
            value.map((option: any, index: any) => (
              <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
            ))
          }
          renderInput={(params: any) => <TextField {...params} label={"Unit-types"} />}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Visit Price"
          type="number"
          value={formData.visit_price}
          onChange={(e) => setFormData({ ...formData, visit_price: parseFloat(e.target.value) || 0 })}
          InputProps={{
            endAdornment: <Typography sx={{ ml: 1 }}>ETB</Typography>
          }}
        />
        <TextField
          fullWidth
          label="Amenities"
          name="amenities"
          value={formData.amenities.join(", ") || ""}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              amenities: e.target.value.split(",").map((item) => item.trim())
            }))
          }
          placeholder="Pool, Gym, Parking"
        />

        {/* Upload project featured image */}
        <Button variant="outlined" component="label" sx={{ mt: 2 }}>
          Upload Featured Image
          <input hidden accept="image/*" type="file" onChange={handleProjectImageChange} />
        </Button>
        {/* Featured Image Preview */}
        {formData.projectImage && (
          <Box sx={{ mt: 2, position: 'relative', width: 150 }}>
            <img src={URL.createObjectURL(formData.projectImage)} alt="Project Featured" width="100%" />
            <Button size="small" onClick={removeProjectImage} sx={{ position: 'absolute', top: 0, right: 0 }}>×</Button>
          </Box>
        )}
        {formData.existingProjectImageUrl && (
          <Box sx={{ mt: 2, position: 'relative', width: 150 }}>
            <img src={formData.existingProjectImageUrl} alt="Existing Project Featured" width="100%" />
            <Button size="small" onClick={removeProjectImage} sx={{ position: 'absolute', top: 0, right: 0 }}>×</Button>
          </Box>
        )}
        {/* Upload additional images */}
        <Button variant="outlined" component="label" sx={{ mt: 2, ml: 2 }}>
          Upload Images
          <input hidden multiple accept="image/*" type="file" onChange={handleImageChange} />
        </Button>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {/* Existing and new gallery images */}
          {formData.existingImages.map((img) => (
            <Box key={img.id} sx={{ position: 'relative' }}>
              <img src={img.url} alt="existing" width={100} height={100} />
              <Button size="small" onClick={() => removeExistingImage(img.id)} sx={{ position: 'absolute', top: 0, right: 0 }}>×</Button>
            </Box>
          ))}
          {formData.images.map((file, index) => (
            <Box key={index} sx={{ position: 'relative' }}>
              <img src={URL.createObjectURL(file)} alt="preview" width={100} height={100} />
              <Button size="small" onClick={() => removeNewImage(index)} sx={{ position: 'absolute', top: 0, right: 0 }}>×</Button>
            </Box>
          ))}
        </Box>

        {/* Legal Documents Section - Only shown when creating new project */}
        {!project && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>Legal Documents</Typography>
            {formData.legalDocuments.map((doc, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Document {index + 1}</Typography>
                  {index > 0 && (
                    <Button size="small" color="error" onClick={() => removeLegalDocument(index)}>
                      Remove
                    </Button>
                  )}
                </Box>
                <TextField
                  fullWidth
                  margin="dense"
                  label="Title"
                  value={doc.title}
                  onChange={(e) => handleLegalDocumentChange(index, 'title', e.target.value)}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  label="Description"
                  multiline
                  rows={2}
                  value={doc.description}
                  onChange={(e) => handleLegalDocumentChange(index, 'description', e.target.value)}
                />
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ mt: 1 }}
                  fullWidth
                >
                  Upload PDF Document
                  <input
                    hidden
                    accept=".pdf"
                    type="file"
                    onChange={(e) => handleLegalDocumentFileChange(index, e)}
                  />
                </Button>
                {doc.filePath && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {doc.filePath.name}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {submissionError && (
          <Alert severity="error" onClose={handleCloseError} sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditProjects;